/**
 * useBottomPanelAutoOpen — Shared panel auto-open behavior for designer nodes
 *
 * Implements two useEffect hooks:
 * 1. Selection effect: open on select (reset to 'config'), close on deselect
 * 2. Regeneration effect: open to 'variants' when running + batchPending > 0
 *
 * ## Press-to-drag must not open the panel
 *
 * ReactFlow (v11 defaults: `selectNodesOnDrag`, `nodeDragThreshold: 0`)
 * selects a node inside d3-drag's `start` handler — i.e. on pointer DOWN,
 * before the node's `dragging` prop turns true on the first move. Opening
 * the panel on that selection mounted it mid-press; the taller node and the
 * panel's reveal-pan (`UnifiedBottomPanel`) then shifted the node under the
 * cursor while it was being dragged (post 1629067388850558).
 *
 * So a selection made by pressing on this node defers the decision to the
 * pointer release: moved <= CLICK_SLOP_PX is a click and opens the panel;
 * anything further is a drag and leaves it as it was. Selection that does
 * not come from a press on the node (keyboard, programmatic, Director focus)
 * still opens immediately. Presses on `.nodrag` controls are ignored — they
 * are not drag handles and open the panel themselves.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import { useCallback, useEffect, useRef } from 'react'
import { useNodeId } from 'reactflow'
import { createNadDebug } from '../../../../utils/debugLog'
import { useCanShowSingleNodeDetails } from './SingleNodeDetailsContext'

const autoOpenLog = createNadDebug('dw:bottom-panel:auto-open')

/** Max pointer travel (CSS px) between press and release that still counts as a click. */
export const CLICK_SLOP_PX = 4

interface NodePress {
  pointerId: number
  x: number
  y: number
}

interface UseBottomPanelAutoOpenOptions {
  selected: boolean | undefined
  isRunning: boolean
  batchPending: number
  setBottomPanelOpen: React.Dispatch<React.SetStateAction<boolean>>
  setActiveTab: (tab: string) => void
  defaultTab?: string
}

/**
 * Wire up the standard bottom panel auto-open lifecycle for designer nodes.
 *
 * - When `selected` becomes true and not running: open panel, reset tab to 'config' if it was closed
 *   (deferred to pointer release when the selection came from pressing the node — see above)
 * - When `selected` becomes false: close panel
 * - When `status === 'running'` and `batchPending > 0`: open panel to 'variants'
 */
export function useBottomPanelAutoOpen({
  selected,
  isRunning,
  batchPending,
  setBottomPanelOpen,
  setActiveTab,
  defaultTab,
}: UseBottomPanelAutoOpenOptions): void {
  const hasAutoSwitchedRef = useRef(false)
  const prevRunningRef = useRef(false)
  const canShowDetails = useCanShowSingleNodeDetails()
  const nodeId = useNodeId()
  const pressRef = useRef<NodePress | null>(null)
  // A press on this node ended without a click (drag release or cancel). Held until the selection
  // effect next runs, so a selection that renders after the release is still
  // classified as a drag. Cleared by the next press.
  const dragReleasedRef = useRef(false)
  const selectedRef = useRef(selected)
  selectedRef.current = selected
  const canShowDetailsRef = useRef(canShowDetails)
  canShowDetailsRef.current = canShowDetails

  const openPanel = useCallback(() => {
    setBottomPanelOpen((wasOpen) => {
      if (!wasOpen) setActiveTab(defaultTab ?? 'config')
      return true
    })
  }, [setBottomPanelOpen, setActiveTab, defaultTab])

  // Auto-open panel on selection
  useEffect(() => {
    const dragReleased = dragReleasedRef.current
    dragReleasedRef.current = false
    if (selected && canShowDetails) {
      if (dragReleased) {
        autoOpenLog({ event: 'auto-open:suppressed-late-drag', node_id: nodeId ?? 'unknown' })
        return
      }
      if (pressRef.current) {
        // Selected by the press itself — click vs drag is decided on release.
        autoOpenLog({ event: 'auto-open:deferred-to-release', node_id: nodeId ?? 'unknown' })
        return
      }
      openPanel()
    } else {
      setBottomPanelOpen(false)
    }
  }, [selected, canShowDetails, setBottomPanelOpen, openPanel, nodeId])

  // Track presses on this node's own body (capture phase on window, so this
  // runs before ReactFlow's d3-drag mousedown selects the node).
  useEffect(() => {
    if (!nodeId) return

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null
      const pressedNodeId = target?.closest('.react-flow__node')?.getAttribute('data-id')
      dragReleasedRef.current = false
      if (event.button !== 0 || pressedNodeId !== nodeId || target?.closest('.nodrag')) {
        pressRef.current = null
        return
      }
      pressRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY }
    }

    const handlePointerUp = (event: PointerEvent) => {
      const press = pressRef.current
      if (!press || press.pointerId !== event.pointerId) return
      pressRef.current = null
      const distance = Math.hypot(event.clientX - press.x, event.clientY - press.y)
      if (distance > CLICK_SLOP_PX) {
        dragReleasedRef.current = true
        autoOpenLog({
          event: 'auto-open:suppressed-drag',
          node_id: nodeId,
          distance: Math.round(distance),
        })
        return
      }
      if (!selectedRef.current || !canShowDetailsRef.current) return
      autoOpenLog({ event: 'auto-open:click-release', node_id: nodeId, distance: Math.round(distance) })
      openPanel()
    }

    const handlePointerCancel = (event: PointerEvent) => {
      if (pressRef.current?.pointerId !== event.pointerId) return
      // A cancelled press never became a click — treat it like a drag release.
      pressRef.current = null
      dragReleasedRef.current = true
    }

    window.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('pointerup', handlePointerUp, true)
    window.addEventListener('pointercancel', handlePointerCancel, true)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('pointerup', handlePointerUp, true)
      window.removeEventListener('pointercancel', handlePointerCancel, true)
    }
  }, [nodeId, openPanel])

  // Auto-open variants tab on regeneration
  useEffect(() => {
    if (isRunning && batchPending > 0 && canShowDetails && !hasAutoSwitchedRef.current) {
      setActiveTab('variants')
      setBottomPanelOpen(true)
      hasAutoSwitchedRef.current = true
    }
  }, [isRunning, batchPending, canShowDetails, setBottomPanelOpen, setActiveTab])

  // Reset auto-switch ref when generation ends
  useEffect(() => {
    if (prevRunningRef.current && !isRunning) {
      hasAutoSwitchedRef.current = false
    }
    prevRunningRef.current = isRunning
  }, [isRunning])
}
